import { Plus } from 'lucide-react'
import { Badge } from '../ui/badge'
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TabsHeader,
  TabsHeaderActions,
  TabsHeaderTitle,
} from '../ui'
import type { CompaniesFundsFilter } from '../../utils/hooks/useCompaniesFundsFilter'
import { cn } from '../../lib/utils'

export interface CompaniesHeaderProps {
  title?: string
  showCreateButton?: boolean
  showAssignButton?: boolean
  onCreateClick?: () => void
  onAssignClick?: () => void
  hideAssignButton?: boolean
  fundsFilter?: CompaniesFundsFilter
  onFundsFilterChange?: (filter: CompaniesFundsFilter) => void
}

const activeClassName = (condition: boolean) => {
  if (condition) {
    return 'border-main text-main-foreground bg-main'
  }
  return 'bg-white text-card-text border-card-border hover:bg-gray-100'
}

function BadgeWrapper({
  children,
  onClick,
  isActive,
}: {
  children: React.ReactNode
  onClick: () => void
  isActive: boolean
}) {
  return (
    <Badge
      variant="outline"
      className={cn('cursor-pointer px-2.5 py-2', activeClassName(isActive))}
      onClick={onClick}
    >
      <span className="leading-none">{children}</span>
    </Badge>
  )
}

export function CompaniesHeader({
  title = 'Companies',
  showCreateButton = false,
  showAssignButton = false,
  onCreateClick,
  onAssignClick,
  hideAssignButton = false,
  fundsFilter = 'all',
  onFundsFilterChange,
}: CompaniesHeaderProps) {
  const showFundsFilter = !!onFundsFilterChange

  return (
    <TabsHeader className="flex-wrap gap-2 md:gap-2 lg:gap-2">
      <div className="flex items-center justify-between md:justify-start flex-row gap-2 md:gap-2 lg:gap-6 flex-wrap">
        <TabsHeaderTitle>{title}</TabsHeaderTitle>

        {showFundsFilter && (
          <div className="flex items-center justify-between">
            <div className="hidden md:flex items-center gap-2 flex-wrap mb-2 lg:mb-0">
              <BadgeWrapper
                onClick={() => onFundsFilterChange('all')}
                isActive={fundsFilter === 'all'}
              >
                All
              </BadgeWrapper>
              <BadgeWrapper
                onClick={() => onFundsFilterChange('withFunds')}
                isActive={fundsFilter === 'withFunds'}
              >
                With funds
              </BadgeWrapper>
            </div>

            <div className="md:hidden">
              <Select
                value={fundsFilter}
                onValueChange={(value) => onFundsFilterChange(value as CompaniesFundsFilter)}
              >
                <SelectTrigger className="w-[140px] bg-white">
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="withFunds">With funds</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>
      <TabsHeaderActions>
        {showCreateButton && onCreateClick && (
          <Button variant="main_action" onClick={onCreateClick}>
            <Plus className="h-3 md:h-4 w-3 md:w-4" />
            Create
          </Button>
        )}
        {!hideAssignButton && showAssignButton && onAssignClick && (
          <Button variant="main_action" onClick={onAssignClick}>
            Assign
          </Button>
        )}
      </TabsHeaderActions>
    </TabsHeader>
  )
}
